import api from './api';

/** Result of a graded attempt (POST …/interactions), see BlockInteractionController. */
export interface BlockInteractionResult {
    id: number;
    lessonId: number;
    blockId: string;
    blockType: string;
    attemptNumber: number;
    isCorrect: boolean | null;
    score: string | null;
    scorePercent: number | null;
    feedback: string | null;
    response: Record<string, unknown>;
    createdAt: string;
    /** Revealed after an attempt so the player can mark answers. */
    solution?: {
        correctIndices?: number[];
        acceptedAnswers?: Record<string, string[]>;
        correctOrder?: (number | null)[];
        correctMatches?: [number, string][];
        steps?: Record<string, { index: number; isCorrect: boolean; feedback: string | null }[]>;
        /** sorting: correct category index per flat item index (A4) */
        correctCategories?: number[];
        /** labeling: expected text per label index (A4) */
        labels?: string[];
    };
}

/** A post of a discussion block (A4). */
export interface DiscussionPost {
    id: number;
    body: string;
    status: 'pending' | 'published' | 'rejected';
    parentId: number | null;
    authorId: number;
    authorName: string | null;
    isMine: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface PollResults {
    options: { index: number; votes: number }[];
    totalVoters: number;
    myVote: number[];
    showResults: boolean;
}

/**
 * Learner-side endpoints of block lessons: graded attempts and poll votes.
 */
export const blockInteractionService = {
    async submit(lessonId: number, blockId: string, response: Record<string, unknown>): Promise<BlockInteractionResult> {
        const { data } = await api.post(`/lessons/${lessonId}/blocks/${encodeURIComponent(blockId)}/interactions`, { response });
        return data;
    },

    async list(lessonId: number, blockId: string): Promise<BlockInteractionResult[]> {
        const { data } = await api.get(`/lessons/${lessonId}/blocks/${encodeURIComponent(blockId)}/interactions`);
        return data['hydra:member'] ?? data.member ?? [];
    },

    async pollResults(lessonId: number, blockId: string): Promise<PollResults> {
        const { data } = await api.get(`/lessons/${lessonId}/blocks/${encodeURIComponent(blockId)}/poll`);
        return data;
    },

    async vote(lessonId: number, blockId: string, options: number[]): Promise<PollResults> {
        const { data } = await api.post(`/lessons/${lessonId}/blocks/${encodeURIComponent(blockId)}/poll/vote`, { options });
        return data;
    },

    async discussion(lessonId: number, blockId: string): Promise<{ posts: DiscussionPost[]; canModerate: boolean }> {
        const { data } = await api.get(`/lessons/${lessonId}/blocks/${encodeURIComponent(blockId)}/discussion`);
        return data;
    },

    async post(lessonId: number, blockId: string, body: string, parentId: number | null = null): Promise<DiscussionPost> {
        const { data } = await api.post(`/lessons/${lessonId}/blocks/${encodeURIComponent(blockId)}/discussion`, parentId === null ? { body } : { body, parentId });
        return data;
    },

    async moderate(lessonId: number, blockId: string, postId: number, status: 'published' | 'rejected'): Promise<DiscussionPost> {
        const { data } = await api.patch(`/lessons/${lessonId}/blocks/${encodeURIComponent(blockId)}/discussion/${postId}/moderate`, { status });
        return data;
    },

    async deletePost(lessonId: number, blockId: string, postId: number): Promise<void> {
        await api.delete(`/lessons/${lessonId}/blocks/${encodeURIComponent(blockId)}/discussion/${postId}`);
    },
};

export default blockInteractionService;
